import React from 'react';
import clsx from 'clsx';
import BlurIcon from '@material-ui/icons/BlurOnOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import NoneIcon from '@material-ui/icons/NotInterestedOutlined';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

export type Thumbnail = 'none' | 'blur' | 'image';

interface BackgroundThumbnailProps {
	thumbnail: Thumbnail;
	imagePath?: string;
	name?: string;
	index?: number;
	/** 'card' renders a wide labelled option (used for None and Blur); 'tile' renders a picture with a label beneath. */
	variant?: 'tile' | 'card';
	className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			gap: 6,
			alignItems: 'stretch',
			padding: 0,
			margin: 0,
			border: 0,
			background: 'transparent',
			font: 'inherit',
			color: theme.palette.text.primary,
			textAlign: 'center',
			cursor: 'pointer',
			minWidth: 0,
			borderRadius: 12,
			WebkitTapHighlightColor: 'transparent',
			'&:focus': {
				outline: 'none',
			},
			'&:focus-visible': {
				outline: `3px solid ${theme.palette.primary.main}`,
				outlineOffset: 2,
			},
			'&:hover $tile, &:hover $card': {
				borderColor: theme.palette.text.secondary,
			},
			'&.selected $tile, &.selected $card': {
				borderColor: theme.palette.primary.main,
				boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
			},
			'&.selected $card': {
				background: '#EAF4FB',
			},
			'&.selected $label, &.selected $cardLabel': {
				color: theme.palette.primary.main,
				fontWeight: 600,
			},
			'&.selected $icon': {
				color: theme.palette.primary.main,
			},
		},
		tile: {
			position: 'relative',
			display: 'block',
			width: '100%',
			paddingTop: '56.25%',
			borderRadius: 10,
			overflow: 'hidden',
			border: `2px solid ${theme.palette.divider}`,
			background: theme.palette.background.default,
			boxSizing: 'border-box',
			transition: theme.transitions.create(
				['border-color', 'box-shadow'],
				{
					duration: theme.transitions.duration.shortest,
				},
			),
		},
		tileInner: {
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		image: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			objectFit: 'cover',
			display: 'block',
		},
		icon: {
			width: 32,
			height: 32,
			color: theme.palette.text.secondary,
		},
		label: {
			display: 'block',
			fontSize: '0.9375rem',
			lineHeight: 1.25,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			padding: '0 2px',
		},
		card: {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
			minHeight: 60,
			padding: '8px 12px',
			borderRadius: 12,
			border: `2px solid ${theme.palette.divider}`,
			background: theme.palette.background.paper,
			boxSizing: 'border-box',
			textAlign: 'left',
			transition: theme.transitions.create(
				['border-color', 'box-shadow', 'background-color'],
				{ duration: theme.transitions.duration.shortest },
			),
		},
		cardIcon: {
			flexShrink: 0,
			width: 40,
			height: 40,
			borderRadius: '50%',
			background: theme.palette.background.default,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		cardLabel: {
			flex: 1,
			minWidth: 0,
			fontSize: '1rem',
			fontWeight: 500,
			lineHeight: 1.3,
		},
		check: {
			flexShrink: 0,
			width: 24,
			height: 24,
			borderRadius: '50%',
			background: theme.palette.primary.main,
			color: '#fff',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: 18,
		},
		tileCheck: {
			position: 'absolute',
			top: 6,
			right: 6,
			boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
		},
	}),
);

export default function BackgroundThumbnail({
	thumbnail,
	imagePath,
	name,
	index,
	variant = 'tile',
	className,
}: BackgroundThumbnailProps) {
	const classes = useStyles();
	const { backgroundSettings, setBackgroundSettings } = useVideoContext();
	const isImage = thumbnail === 'image';
	const thumbnailSelected = isImage
		? backgroundSettings.index === index &&
			backgroundSettings.type === 'image'
		: backgroundSettings.type === thumbnail;
	const icons = {
		none: NoneIcon,
		blur: BlurIcon,
		image: null,
	};
	const ThumbnailIcon = icons[thumbnail];

	return (
		<button
			type="button"
			aria-pressed={thumbnailSelected}
			className={clsx(
				classes.root,
				{ selected: thumbnailSelected },
				className,
			)}
			onClick={() =>
				setBackgroundSettings({
					type: thumbnail,
					index: index,
				})
			}
		>
			{variant === 'card' ? (
				<span className={classes.card}>
					{ThumbnailIcon && (
						<span className={classes.cardIcon} aria-hidden="true">
							<ThumbnailIcon className={classes.icon} />
						</span>
					)}
					<span className={classes.cardLabel}>{name}</span>
					{thumbnailSelected && (
						<span className={classes.check} aria-hidden="true">
							<CheckIcon fontSize="inherit" />
						</span>
					)}
				</span>
			) : (
				<>
					<span className={classes.tile}>
						{ThumbnailIcon ? (
							<span
								className={classes.tileInner}
								aria-hidden="true"
							>
								<ThumbnailIcon className={classes.icon} />
							</span>
						) : (
							<img
								className={classes.image}
								src={imagePath}
								alt=""
							/>
						)}
						{thumbnailSelected && (
							<span
								className={clsx(
									classes.check,
									classes.tileCheck,
								)}
								aria-hidden="true"
							>
								<CheckIcon fontSize="inherit" />
							</span>
						)}
					</span>
					<span className={classes.label}>{name}</span>
				</>
			)}
		</button>
	);
}
